<script setup lang="ts">
  import BoxCard from '@/components/common/BoxCard/BoxCard.vue';
  import { useI18n } from 'vue-i18n';
  import PhoneNumberWithIcon from '@/components/common/CustomerServiceModal/PhoneNumberWithIcon.vue';
  import ContentWithIcon from '@/components/common/CustomerServiceModal/ContentWithIcon.vue';
  import type { VueComponentPropsBase } from '@/types';
  import useTransitOrganization from '@/composables/useTransitOrganization.ts';

  type CustomerServiceContentClassNames = 'boxCardWrapper';

  defineOptions({ name: 'CustomerServiceContent' });

  defineProps<Partial<VueComponentPropsBase<CustomerServiceContentClassNames>>>();

  const {
    customerSupportPhoneLink: stmSupportPhoneLink,
    customerServicePhoneLink: stmCustomerServicePhoneLink,
    emailAddress: stmEmailAddress,
    customerSupportHoursLabel: stmSupportHoursLabel,
    websiteSupportHoursLabel: stmWebsiteSupportHoursLabel,
    customerServiceHoursLabel: stmCustomerServiceHoursLabel,
  } = useTransitOrganization('stm');

  const {
    customerSupportPhoneLink: stlSupportPhoneLink,
    customerServicePhoneLink: stlCustomerServicePhoneLink,
    customerSupportHoursLabel: stlSupportHoursLabel,
    websiteSupportHoursLabel: stlWebsiteSupportHoursLabel,
    customerServiceHoursLabel: stlCustomerServiceHoursLabel,
  } = useTransitOrganization('stl');

  const { t } = useI18n();
</script>
<template>
  <div
    class="flex flex-col bg-page-background pt-4 gap-4 text-left"
    :class="classes?.wrapper"
  >
    <BoxCard
      :title="t('contactUsPage.support.title')"
      padded
      :classes="{wrapper: classes?.boxCardWrapper ?? 'width-full'}"
    >
      <div class="@container">
        <div class="flex flex-col gap-6 items-start relative self-stretch @2xl:flex-row @2xl:gap-12">
          <div class="space-y-6 flex-1">
            <h3 class="font-bold">
              {{ t('contactUsPage.support.montreal.title') }}
            </h3>
            <PhoneNumberWithIcon :phone-link="stmSupportPhoneLink" />
            <ContentWithIcon
              icon="time"
              class="items-start"
            >
              <div>{{ t('contactUsPage.support.montreal.openingHours.title') }}</div>
              <ul class="list-disc ml-8 space-y-2 mt-2">
                <li>{{ t('contactUsPage.support.montreal.openingHours.reservationCenter') }}{{ '\n' + stmSupportHoursLabel }}</li>
                <li>{{ t('contactUsPage.support.montreal.openingHours.websiteSupport') }}{{ '\n' + stmWebsiteSupportHoursLabel }}</li>
              </ul>
            </ContentWithIcon>
          </div>
          <div class="hidden @2xl:block w-px border-l-1 absolute top-0 bottom-0 left-1/2" />
          <div class="space-y-6 flex-1">
            <h3 class="font-bold">
              {{ t('contactUsPage.support.laval.title') }}
            </h3>
            <PhoneNumberWithIcon :phone-link="stlSupportPhoneLink" />
            <ContentWithIcon
              icon="time"
              class="items-start"
            >
              <div>{{ t('contactUsPage.support.laval.openingHours.title') }}</div>
              <ul class="list-disc ml-8 space-y-2 mt-2">
                <li>{{ t('contactUsPage.support.laval.openingHours.reservationCenter') }}{{ '\n' + stlSupportHoursLabel }}</li>
                <li>{{ t('contactUsPage.support.laval.openingHours.websiteSupport') }}{{ '\n' + stlWebsiteSupportHoursLabel }}</li>
              </ul>
            </ContentWithIcon>
          </div>
        </div>
      </div>
    </BoxCard>
    <BoxCard
      :title="t('contactUsPage.sac.title')"
      padded
      :classes="{wrapper: classes?.boxCardWrapper ?? 'width-full'}"
    >
      <div class="@container">
        <div class="flex flex-col gap-6 items-start relative self-stretch @2xl:flex-row @2xl:gap-12">
          <div class="space-y-6 flex-1">
            <h3 class="font-bold">
              {{ t('contactUsPage.sac.montreal.title') }}
            </h3>
            <div class="flex flex-col gap-4">
              <PhoneNumberWithIcon :phone-link="stmCustomerServicePhoneLink" />
              <ContentWithIcon
                v-if="stmEmailAddress"
                icon="email"
              >
                <a :href="`mailto:${stmEmailAddress}`">{{ stmEmailAddress }}</a>
              </ContentWithIcon>
              <ContentWithIcon
                :text="`${t('contactUsPage.sac.montreal.openingHours')}\n${stmCustomerServiceHoursLabel}`"
                icon="time"
              />
            </div>
          </div>
          <div class="hidden @2xl:block w-px border-l-1 absolute top-0 bottom-0 left-1/2" />
          <div class="space-y-6 flex-1">
            <h3 class="font-bold">
              {{ t('contactUsPage.sac.laval.title') }}
            </h3>
            <div class="flex flex-col gap-4">
              <PhoneNumberWithIcon :phone-link="stlCustomerServicePhoneLink" />
              <ContentWithIcon
                :text="`${t('contactUsPage.sac.laval.openingHours')}\n${stlCustomerServiceHoursLabel}`"
                icon="time"
              />
            </div>
          </div>
        </div>
      </div>
    </BoxCard>
  </div>
</template>
